import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  FileText,
  Globe,
  StickyNote,
  Quote,
  Upload,
  ChevronsUpDown,
  Check,
} from 'lucide-react';
import { ResearchItem } from '@/hooks/useResearch';
import { Chapter, Section } from '@/hooks/useChapters';
import { cn } from '@/lib/utils';

const typeIcons: Record<string, React.ElementType> = {
  note: StickyNote,
  upload: Upload,
  web_snapshot: Globe,
  quote: Quote,
};

interface AddCitationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  researchItems: ResearchItem[];
  chapters: Chapter[];
  sections: Record<string, Section[]>;
  onAdd: (
    researchItemId: string,
    sectionId?: string,
    citationText?: string,
    pageReference?: string,
    isDirectQuote?: boolean
  ) => Promise<void>;
  preselectedItemId?: string;
}

export function AddCitationDialog({
  isOpen,
  onClose,
  researchItems,
  chapters,
  sections,
  onAdd,
  preselectedItemId,
}: AddCitationDialogProps) {
  const [selectedItemId, setSelectedItemId] = useState<string | null>(preselectedItemId || null);
  const [sectionId, setSectionId] = useState<string | null>(null);
  const [citationText, setCitationText] = useState('');
  const [pageReference, setPageReference] = useState('');
  const [isDirectQuote, setIsDirectQuote] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [comboboxOpen, setComboboxOpen] = useState(false);

  // Filter out user-authored items
  const citableItems = useMemo(() => 
    researchItems.filter(item => !item.is_authored_by_user),
    [researchItems]
  );

  const selectedItem = selectedItemId 
    ? citableItems.find(i => i.id === selectedItemId) 
    : null;

  const handleAdd = async () => {
    if (!selectedItemId) return;
    
    setIsAdding(true);
    try {
      await onAdd(
        selectedItemId,
        sectionId || undefined,
        citationText || undefined,
        pageReference || undefined,
        isDirectQuote
      );
      // Reset form
      setSelectedItemId(null);
      setSectionId(null);
      setCitationText('');
      setPageReference('');
      setIsDirectQuote(false);
      onClose();
    } finally {
      setIsAdding(false);
    }
  };

  // Build flat list of sections with chapter context
  const sectionOptions: { id: string; label: string; chapterTitle: string }[] = [];
  chapters.forEach((chapter) => {
    const chapterSections = sections[chapter.id] || [];
    chapterSections.forEach((section) => {
      sectionOptions.push({
        id: section.id,
        label: section.title,
        chapterTitle: chapter.title,
      });
    });
  });

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Citation</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Research Item Selector */}
          <div className="space-y-2">
            <Label>Select Research Item</Label>
            <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={comboboxOpen}
                  className="w-full justify-between"
                >
                  {selectedItem ? (
                    <div className="flex items-center gap-2 truncate">
                      {(() => {
                        const Icon = typeIcons[selectedItem.type] || FileText;
                        return <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />;
                      })()}
                      <span className="truncate">
                        {selectedItem.source_author || selectedItem.title}
                      </span>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">Select a source...</span>
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search sources..." />
                  <CommandList>
                    <CommandEmpty>No sources found.</CommandEmpty>
                    <CommandGroup>
                      {citableItems.map((item) => {
                        const Icon = typeIcons[item.type] || FileText;
                        return (
                          <CommandItem
                            key={item.id}
                            value={item.title + (item.source_author || '')}
                            onSelect={() => {
                              setSelectedItemId(item.id);
                              setComboboxOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedItemId === item.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
                            <div className="truncate">
                              <span className="font-medium">
                                {item.source_author || item.title}
                              </span>
                              {item.source_author && (
                                <span className="text-muted-foreground ml-1.5">
                                  - {item.title}
                                </span>
                              )}
                            </div>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          
          {/* Section Assignment */}
          <div className="space-y-2">
            <Label>Assign to Section (optional)</Label>
            <Select
              value={sectionId || 'unassigned'}
              onValueChange={(value) => setSectionId(value === 'unassigned' ? null : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a section" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">
                  <span className="text-muted-foreground">Unassigned</span>
                </SelectItem>
                {sectionOptions.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    <span className="text-muted-foreground">{option.chapterTitle} &rsaquo;</span>{' '}
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Citation Text */}
          <div className="space-y-2">
            <Label htmlFor="add-citation-text">Citation Text (optional)</Label>
            <Textarea
              id="add-citation-text"
              value={citationText}
              onChange={(e) => setCitationText(e.target.value)}
              placeholder="Enter the cited text or summary..."
              className="min-h-[80px]"
            />
          </div>
          
          {/* Page Reference */}
          <div className="space-y-2">
            <Label htmlFor="add-page-ref">Page Reference (optional)</Label>
            <Input
              id="add-page-ref"
              value={pageReference}
              onChange={(e) => setPageReference(e.target.value)}
              placeholder="e.g., 42-45"
            />
          </div>
          
          {/* Direct Quote Checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="add-direct-quote"
              checked={isDirectQuote}
              onCheckedChange={(checked) => setIsDirectQuote(checked === true)}
            />
            <Label htmlFor="add-direct-quote" className="text-sm font-normal">
              This is a direct quote
            </Label>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isAdding}>
            Cancel
          </Button>
          <Button onClick={handleAdd} disabled={!selectedItemId || isAdding}>
            {isAdding ? 'Adding...' : 'Add Citation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
